import { Box } from '@chakra-ui/react';
import { useState, useCallback, useRef } from 'react';
import { useEditorState } from '@tiptap/react';
import ReactSelect from 'react-select';
import { menuBarStateSelector } from './menuBarState.js';

import {
  ToolbarBoldIcon,
  ToolbarImageIcon,
  ToolbarItalicsIcon,
  ToolbarLinkIcon,
  ToolbarStrikeIcon,
  ToolbarUnderlineIcon,
  ToolbarAlignLeftIcon,
  ToolbarAlignCenterIcon,
  ToolbarAlignRightIcon,
} from 'Asset/icon/index.js';
import { reactSelectStyle } from 'UiKit/filter-select/const';

import usePostUserImage from 'Domain/brief-live/usePostUserImage';

import { FONT_SIZE_OPTIONS } from './const.js';

import styles from './styles.module.css';

const ImageInsertPopup = ({ editor, onImageUpload, onClose }) => {
  const [tab, setTab] = useState('upload');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const insertFromUrl = (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    editor.chain().focus().setImage({ src: url.trim() }).run();
    onClose();
  };

  const insertFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { imagePath, key } = await onImageUpload(file);
      editor.chain().focus().setImage({ src: imagePath, alt: file.name, key }).run();
      onClose();
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box className={styles['img-insert-popup']}>
      <Box className={styles['img-insert-tabs']}>
        <button type="button" className={`${styles['img-insert-tab']}${tab === 'upload' ? ` ${styles['active']}` : ''}`} onClick={() => setTab('upload')}>
          Upload
        </button>
        <button type="button" className={`${styles['img-insert-tab']}${tab === 'url' ? ` ${styles['active']}` : ''}`} onClick={() => setTab('url')}>
          URL
        </button>
        <button type="button" className={styles['img-insert-close']} onClick={onClose}>
          ✕
        </button>
      </Box>
      {tab === 'upload' ? (
        <Box className={styles['img-insert-body']}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={insertFromFile} disabled={uploading} />
          <button type="button" className={styles['img-upload-btn']} onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Choose image…'}
          </button>
        </Box>
      ) : (
        <Box className={styles['img-insert-body']}>
          <input
            autoFocus
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && insertFromUrl(e)}
            className={styles['img-url-input']}
          />
          <button type="button" className={styles['img-url-ok']} onClick={insertFromUrl}>
            Insert
          </button>
        </Box>
      )}
    </Box>
  );
};

export const MenuBar = ({ editor }) => {
  const { mutateAsync: uploadImage } = usePostUserImage();
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editorState = useEditorState({
    editor,
    selector: menuBarStateSelector,
  });

  const handleLinkSubmit = useCallback(() => {
    if (!editor) return;
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  if (!editor) return null;

  const activeFontSizeOption = FONT_SIZE_OPTIONS.find((o) => o.value === editorState?.fontSize) ?? FONT_SIZE_OPTIONS[1];

  const handleFontSizeChange = (option) => {
    if (!option?.value) {
      editor.chain().focus().unsetFontSize().run();
    } else {
      editor.chain().focus().setFontSize(option.value).run();
    }
  };

  return (
    <Box className={styles['menubar']}>
      <ReactSelect
        isSearchable={false}
        options={FONT_SIZE_OPTIONS}
        value={activeFontSizeOption}
        onChange={handleFontSizeChange}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          control: (base) => ({
            ...base,
            ...reactSelectStyle.control,
            minHeight: '28px',
            height: '28px',
            fontSize: 'var(--font-body3)',
            backgroundColor: 'var(--color-semantic-background)',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
          }),
          valueContainer: (base) => ({ ...base, padding: '0 6px' }),
          indicatorsContainer: (base) => ({ ...base, height: '28px' }),
          indicatorSeparator: () => ({ display: 'none' }),
          input: (base) => ({ ...base, fontSize: 'var(--font-body3)' }),
          menu: (base) => ({ ...base, ...reactSelectStyle.menu }),
          menuList: (base) => ({ ...base, ...reactSelectStyle.menuList }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          singleValue: (base) => ({ ...base, color: 'var(--color-semantic-text)', fontSize: 'var(--font-body3)', maxWidth: 'none' }),
          option: (base, state) => ({
            ...base,
            fontSize: state?.data?.value,
            backgroundColor: state?.isFocused ? 'var(--color-semantic-background)' : 'transparent',
            color: 'var(--color-semantic-text)',
            cursor: 'pointer',
          }),
        }}
      />

      <button
        type="button"
        className={`${styles['menubar-btn']}${editorState?.isBold ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          editor.chain().focus().toggleBold().run();
        }}
        title="Bold"
      >
        <ToolbarBoldIcon />
      </button>

      <button
        type="button"
        className={`${styles['menubar-btn']} ${styles['menubar-btn--italic']}${editorState?.isItalic ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          editor.chain().focus().toggleItalic().run();
        }}
        title="Italic"
      >
        <ToolbarItalicsIcon />
      </button>

      <button
        type="button"
        className={`${styles['menubar-btn']} ${styles['menubar-btn--underline']}${editorState?.isUnderline ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          editor.chain().focus().toggleUnderline().run();
        }}
        title="Underline"
      >
        <ToolbarUnderlineIcon />
      </button>

      <button
        type="button"
        className={`${styles['menubar-btn']} ${styles['menubar-btn--strike']}${editorState?.isStrike ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          editor.chain().focus().toggleStrike().run();
        }}
        title="Strikethrough"
      >
        <ToolbarStrikeIcon />
      </button>

      <button
        type="button"
        className={`${styles['menubar-btn']}${editorState?.isAlignLeft ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          editor.chain().focus().setTextAlign('left').run();
        }}
        title="Align left"
      >
        <ToolbarAlignLeftIcon />
      </button>

      <button
        type="button"
        className={`${styles['menubar-btn']}${editorState?.isAlignCenter ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          editor.chain().focus().setTextAlign('center').run();
        }}
        title="Align center"
      >
        <ToolbarAlignCenterIcon />
      </button>

      <button
        type="button"
        className={`${styles['menubar-btn']}${editorState?.isAlignRight ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          editor.chain().focus().setTextAlign('right').run();
        }}
        title="Align right"
      >
        <ToolbarAlignRightIcon />
      </button>

      <Box className={styles['menubar-sep']}></Box>

      <button
        type="button"
        className={`${styles['menubar-btn']}${editorState?.isLink ? ` ${styles['is-active']}` : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          if (editorState?.isLink) {
            editor.chain().focus().unsetLink().run();
          } else {
            setShowLinkInput((v) => !v);
          }
        }}
        title="Link"
      >
        <ToolbarLinkIcon />
      </button>

      <button
        type="button"
        className={styles['menubar-btn']}
        onMouseDown={(e) => {
          e.preventDefault();
          setShowLinkInput(false);
          setShowImagePopup((v) => !v);
        }}
        title="Insert image"
      >
        <ToolbarImageIcon />
      </button>

      {showLinkInput && (
        <Box className={styles['menubar-link-popup']}>
          <input
            autoFocus
            type="url"
            placeholder="https://..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <button type="button" onClick={handleLinkSubmit}>
            OK
          </button>
          <button type="button" onClick={() => setShowLinkInput(false)}>
            ✕
          </button>
        </Box>
      )}

      {showImagePopup && <ImageInsertPopup editor={editor} onImageUpload={(file) => uploadImage({ file })} onClose={() => setShowImagePopup(false)} />}
    </Box>
  );
};
